import type { FC } from "react";

import { AlertBanner } from "@jti/core-components/components/AlertBanner";
import { I18n, QuantityInput } from "@jti/ui";

import styles from "./PromoGifts.module.scss";

export type PromoGiftProduct = {
  sku: string;
  name: string;
  imageUrl?: string;
  quantity: number;
  pending: boolean;
};

export type PromoGiftRule = {
  salesRuleId: number;
  giftQtyLimit: number;
  selectedCount: number;
  products: PromoGiftProduct[];
};

export type PromoGiftsProps = {
  rules: PromoGiftRule[];
  onQuantityChange: (salesRuleId: number, sku: string, value: number) => void;
  orderLimitEnabled?: boolean;
  isOrderLimitReached?: boolean;
  maxOrderLimit?: number;
  currency?: string;
};

export const PromoGifts: FC<PromoGiftsProps> = ({
  rules,
  onQuantityChange,
  orderLimitEnabled,
  isOrderLimitReached,
  maxOrderLimit,
  currency,
}) => {
  if (rules.length === 0) return null;

  return (
    <div className={styles.wrapper}>
      {rules.map((rule) => {
        const hasReachedLimit = rule.selectedCount >= rule.giftQtyLimit;

        return (
          <div key={rule.salesRuleId}>
            <AlertBanner
              data-testid="info-banner-wrapper"
              tone={hasReachedLimit ? "success" : "info"}
              richTitle={
                hasReachedLimit ? (
                  <I18n t="cart.free_gifts.alertEnd">
                    <b>{rule.giftQtyLimit}</b>
                  </I18n>
                ) : (
                  <I18n t="cart.free_gifts.alertMore">
                    <b>{rule.selectedCount}</b>
                    <b>{rule.giftQtyLimit}</b>
                    <b>{rule.giftQtyLimit - rule.selectedCount}</b>
                  </I18n>
                )
              }
            />

            {rule.products.map((product) => (
              <div key={product.sku} className={styles.giftItem}>
                <div className={styles.giftContent}>
                  <div className={styles.description}>
                    {product.imageUrl && <img src={product.imageUrl} alt={product.name} className={styles.image} />}
                    <span className={styles.giftName}>{product.name}</span>
                  </div>

                  <QuantityInput
                    value={product.quantity}
                    min={0}
                    disabled={product.pending}
                    disabledAdd={hasReachedLimit}
                    onChange={(value) => onQuantityChange(rule.salesRuleId, product.sku, value)}
                    orderLimitEnabled={orderLimitEnabled}
                    isOrderLimitReached={isOrderLimitReached}
                    maxOrderLimit={maxOrderLimit}
                    currency={currency}
                  />
                </div>
              </div>
            ))}
          </div>
        );
      })}
    </div>
  );
};
